import React, { useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { variables } from '@splunk/themes';
import MagnifierChartLine from '@splunk/react-icons/MagnifierChartLine';
import Button from '@splunk/react-ui/Button';
import Tooltip from '@splunk/react-ui/Tooltip';
import { ThemeProvider, useLegacyThemeServiceKey } from '../../theme/ThemeProvider';
import CrosslinkService from '../../crosslinks/crosslinkService';
import { AngularInjector } from '../../AngularUtils';
import { trackNavigatorChartDrilldownLinkClick } from '../../telemetry';
import { UserAnalytics } from '@splunk/olly-services/lib/services/UserAnalytics/UserAnalytics';

const StyledMagnifierIcon = styled(MagnifierChartLine)`
    width: 20px;
    height: 20px;
    color: ${variables.contentColorDefault};
`;

const StyledButton = styled(Button)`
    color: ${variables.contentColorDefault} !important;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    &:hover {
        color: ${variables.contentColorActive} !important;
        svg {
            color: ${variables.contentColorActive};
        }
    }
`;

type ExternalLink = {
    type: 'StaticExternal';
    label: string;
    url: string;
};

type InternalLink = {
    type: 'StaticInternal';
    label: string;
    path: string;
};

type NavigatorLink = {
    type: 'NavigatorLink';
    label: string;
    destinationDashboardId: string;
    isDefault: boolean;
    metricClassCode: string;
    navigatorCode: string;
    navigatorId: string;
    navigatorViewType: string;
    navigatorVisualizationType: string;
    navigatorPropertyIdentifierTemplate?: string;
    idName?: string;
    colorBy: string;
    orderBy: [];
};

export type Link = ExternalLink | InternalLink | NavigatorLink;
export type SuggestionEntry = { name: string; value: string };

type DrilldownButtonProps = {
    link: Link;
    suggestions?: Array<SuggestionEntry>;
};

export const resolveLink = (link: Link, getRedirectHref?: any): string => {
    if (link?.type === 'StaticInternal') return link?.path;
    if (link?.type === 'StaticExternal') return link?.url;
    if (link?.type === 'NavigatorLink') return getRedirectHref(link);

    const exhaustiveCheck: never = link;
    throw new Error(`Unhandled link type: ${exhaustiveCheck}`);
};

function handleDrilldownButtonClick(link: Link, userAnalytics: UserAnalytics): void {
    if (link?.type === 'NavigatorLink') {
        trackNavigatorChartDrilldownLinkClick(userAnalytics, link?.navigatorCode);
    }
}

export const DrilldownButton = ({ link, suggestions }: DrilldownButtonProps): React.JSX.Element => {
    const themeKey = useLegacyThemeServiceKey();
    const navigatorModes = useMemo(() => AngularInjector.instantiate('navigatorModes'), []);
    const [chartLink, setChartLink] = useState<any>({});
    const crosslinkService = CrosslinkService.useInstance();
    const dashboardUtil = AngularInjector.useInjectedClass('dashboardUtil');
    const featureEnabled = AngularInjector.useInjectedClass('featureEnabled');
    const crosslinkUtils = crosslinkService.getUtils();
    const { getRedirectHref } = crosslinkUtils;
    const label = link?.label;
    const userAnalytics = AngularInjector.useInjectedClass('userAnalytics');
    const pathname = window.location.hash.split('?')[0];
    const navigatorId = pathname?.split('/')[3];

    const getNavigatorModes = useCallback(async () => {
        try {
            const response = await navigatorModes.getModes();
            if (!Array.isArray(response)) {
                throw new Error(`Invalid response format:${response}`);
            }

            const chartDrilldownLink = {
                navigatorPropertyIdentifierTemplate: null,
                idName: null,
                ...link,
            };
            if (link?.type === 'NavigatorLink' && navigatorId) {
                const filterNavigator = response.find((r: any) => r?.id === navigatorId);
                if (filterNavigator) {
                    chartDrilldownLink.navigatorPropertyIdentifierTemplate =
                        filterNavigator?.idTemplate;
                    chartDrilldownLink.idName = filterNavigator?.idName;
                }
            }

            setChartLink(chartDrilldownLink as NavigatorLink);
        } catch (err) {
            if (err.message) {
                throw new Error(`HTTP error! status: ${err.status}`);
            }
        }
    }, [link, navigatorId, navigatorModes]);

    useEffect(() => {
        getNavigatorModes();
    }, [getNavigatorModes]);

    return (
        <ThemeProvider colorScheme={themeKey}>
            {(link?.type !== 'NavigatorLink' || featureEnabled('chartDrillDownForNavigators')) &&
                chartLink &&
                Object.keys(chartLink).length && (
                    <Tooltip
                        contentRelationship="label"
                        content={<div className="drilldown-button-tooltip-label">{label}</div>}
                        defaultPlacement="above"
                    >
                        <StyledButton
                            to={
                                suggestions
                                    ? dashboardUtil.resolveUrlWithVariables(
                                          resolveLink(link, getRedirectHref),
                                          suggestions
                                      )
                                    : resolveLink(chartLink, getRedirectHref)
                            }
                            appearance="subtle"
                            openInNewContext={link?.type === 'StaticExternal'}
                            icon={<StyledMagnifierIcon />}
                            label={label}
                            onClick={(): void => {
                                handleDrilldownButtonClick(chartLink, userAnalytics);
                            }}
                        />
                    </Tooltip>
                )}
        </ThemeProvider>
    );
};
